import React from 'react';
import {
  AbsoluteFill,
  Sequence,
  Img,
  OffthreadVideo,
  Audio,
  useVideoConfig,
  useRemotionEnvironment,
} from 'remotion';
import { Video as MediaVideo } from '@remotion/media';
import { layerRectToStyle } from '@/lib/layers/layerRectToStyle';
import { computeReframeTransform } from '@/lib/layers/computeReframeTransform';
import { hexToRgba } from '@/lib/layers/hexToRgba';
import { ClipVideoSequences, type FrameSequence, type ClipInterval } from '@/components/layouts/ClipVideoSequences';
import type { LayerOverlayItem, TextLayerStyle } from '@/lib/layers/types';
import { useLayerFont } from '@/hooks/use-layer-font';
import { designScale } from '@/lib/render/designScale';
import { MotionSceneRenderer } from '@/components/layers/motion/MotionSceneRenderer';

/** Context needed to render a clip-mirror layer: the same video source and
 *  timing the main view uses, so the mirror stays frame-synced. */
export interface LayerOverlayClipContext {
  videoUrl: string;
  frameSequences?: FrameSequence[];
  intervals?: ClipInterval[];
  playback?: 'html5' | 'media';
  previewPlaying?: boolean;
}

const TextContent: React.FC<{ text: string; style: TextLayerStyle }> = ({ text, style }) => {
  useLayerFont(style.fontName, style.fontUrl);
  const { width, height } = useVideoConfig();
  const scale = designScale(width, height);

  // The background box hugs the text (padding + radius) rather than filling the
  // whole layer rect, matching the subtitle highlight box. backgroundOpacity
  // rides on the box color so it never fades the text itself.
  const background = style.backgroundColor
    ? hexToRgba(style.backgroundColor, style.backgroundOpacity)
    : 'transparent';

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent:
          style.align === 'left' ? 'flex-start' : style.align === 'right' ? 'flex-end' : 'center',
        overflow: 'hidden',
      }}
    >
      <span
        data-testid="layer-text"
        style={{
          maxWidth: '100%',
          boxSizing: 'border-box',
          fontFamily: `"${style.fontName}", sans-serif`,
          fontSize: style.fontSize * scale,
          fontWeight: style.bold ? 700 : 400,
          fontStyle: style.italic ? 'italic' : 'normal',
          color: style.color,
          backgroundColor: background,
          borderRadius: style.borderRadius * scale,
          padding: `${style.paddingY * scale}px ${style.paddingX * scale}px`,
          WebkitTextStroke:
            style.strokeColor && style.strokeWidth > 0
              ? `${style.strokeWidth * scale}px ${style.strokeColor}`
              : undefined,
          textShadow: style.shadow
            ? `${style.shadow.x * scale}px ${style.shadow.y * scale}px ${style.shadow.blur * scale}px ${style.shadow.color}`
            : undefined,
          textAlign: style.align,
          whiteSpace: 'pre-wrap',
        }}
      >
        {text}
      </span>
    </div>
  );
};

/** Renders one positioned media layer inside a Remotion composition.
 *  Only fires for items carrying a `layer` marker; everything else is ignored,
 *  keeping legacy overlays on their existing code path. Timing (start/end) is
 *  handled by the parent <Sequence>. */
const LayerOverlay: React.FC<{ overlay: LayerOverlayItem; clipContext?: LayerOverlayClipContext }> = ({
  overlay,
  clipContext,
}) => {
  const { fps, width, height } = useVideoConfig();
  // @remotion/web-renderer (the editor's in-browser cover still) throws on
  // <OffthreadVideo> and the HTML5 <Audio>: there, video layers render through
  // @remotion/media and audio is skipped — a still has no sound. Export and
  // the Player keep their existing components.
  const { isClientSideRendering } = useRemotionEnvironment();
  if (!overlay.layer) return null;

  if (overlay.type === 'audio') {
    if (!overlay.url || isClientSideRendering) return null;
    return (
      <Audio
        src={overlay.url}
        volume={overlay.layer.volume}
        playbackRate={overlay.layer.speed ?? 1}
        startFrom={Math.round((overlay.layer.trimStart ?? 0) * fps)}
      />
    );
  }

  if (!overlay.layer.rect) return null;
  const { rect, rotation, opacity } = overlay.layer;
  const style = layerRectToStyle({ rect, rotation, opacity });

  if (overlay.type === 'motion') {
    return (
      <AbsoluteFill>
        <div style={style} data-testid="layer-motion-placement">
          <div style={{ width, height, transform: `scale(${rect.w}, ${rect.h})`, transformOrigin: '0 0' }}>
            <MotionSceneRenderer scene={overlay.scene} />
          </div>
        </div>
      </AbsoluteFill>
    );
  }

  if (overlay.type === 'clip') {
    if (!clipContext?.videoUrl) return null;
    const startFrames = Math.round(overlay.start * fps);
    const reframe = overlay.layer.frame ? computeReframeTransform(overlay.layer.frame) : undefined;
    const inner = clipContext.frameSequences
      ? (
        <ClipVideoSequences
          videoUrl={clipContext.videoUrl}
          frameSequences={clipContext.frameSequences.map((s) => ({ ...s, from: s.from - startFrames }))}
          fps={fps}
          volume={overlay.layer.volume}
          objectFit="cover"
        />
      )
      : (
        <Sequence from={-startFrames}>
          <ClipVideoSequences
            videoUrl={clipContext.videoUrl}
            intervals={clipContext.intervals ?? []}
            playback={clipContext.playback}
            previewPlaying={clipContext.previewPlaying}
            fps={fps}
            volume={overlay.layer.volume}
            objectFit="cover"
          />
        </Sequence>
      );
    return (
      <AbsoluteFill>
        <div style={{ ...style, overflow: 'hidden' }}>
          {reframe ? <div style={{ position: 'absolute', inset: 0, ...reframe }}>{inner}</div> : inner}
        </div>
      </AbsoluteFill>
    );
  }

  if (overlay.type === 'image') {
    if (!overlay.url) return null;
    return (
      <AbsoluteFill>
        <Img src={overlay.url} style={style} />
      </AbsoluteFill>
    );
  }

  if (overlay.type === 'video') {
    if (!overlay.url) return null;
    const reframe = overlay.layer.frame ? computeReframeTransform(overlay.layer.frame) : undefined;
    const playbackRate = overlay.layer.speed ?? 1;
    const trimFrames = Math.round((overlay.layer.trimStart ?? 0) * fps);
    const renderVideo = (videoStyle: React.CSSProperties, objectFit: 'cover' | 'contain') =>
      isClientSideRendering ? (
        <MediaVideo
          src={overlay.url}
          muted
          playbackRate={playbackRate}
          trimBefore={trimFrames}
          objectFit={objectFit}
          style={videoStyle}
        />
      ) : (
        <OffthreadVideo
          src={overlay.url}
          volume={overlay.layer.volume}
          playbackRate={playbackRate}
          startFrom={trimFrames}
          style={{ ...videoStyle, objectFit }}
        />
      );
    if (reframe) {
      return (
        <AbsoluteFill>
          <div style={{ ...style, overflow: 'hidden' }}>
            <div style={{ position: 'absolute', inset: 0, ...reframe }}>
              {renderVideo({ width: '100%', height: '100%' }, 'cover')}
            </div>
          </div>
        </AbsoluteFill>
      );
    }
    return <AbsoluteFill>{renderVideo(style, 'contain')}</AbsoluteFill>;
  }

  if (overlay.type === 'text') {
    if (!overlay.text || !overlay.style) return null;
    return (
      <AbsoluteFill>
        <div style={style}>
          <TextContent text={overlay.text} style={overlay.style} />
        </div>
      </AbsoluteFill>
    );
  }

  return null;
};

export default LayerOverlay;
